import { Autocomplete, Box, Checkbox, Stack } from '@mui/material';
import { TextField } from '@side/pantry';
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { InactiveChip } from './InactiveChip';
import { useChunkedTeams } from './useChunkedIdentity';

interface Props<T extends Record<string, unknown>> {
  control: Control<T, unknown, T>;
  name: Path<T>;
  teamIdentityIds?: string[];
}

export const TeamFilter = <T extends FieldValues>({
  control,
  name,
  teamIdentityIds = [],
}: Props<T>) => {
  const { teams, isLoading } = useChunkedTeams(teamIdentityIds);
  const teamOptions = [...(teams ?? [])]
    .sort((a, b) =>
      (a.alias ?? a.legalName ?? '').localeCompare(
        b.alias ?? b.legalName ?? '',
      ),
    )
    .map((team) => ({
      label: team.alias ?? team.legalName ?? '',
      value: team.id,
      status: team.status,
    }));

  return (
    <Controller
      name={name}
      disabled={isLoading}
      control={control}
      render={({ field: { value, onChange } }) => (
        <Autocomplete
          disabled={isLoading}
          multiple
          options={teamOptions}
          value={value}
          onChange={(_event, newValue) => onChange(newValue)}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.label
          }
          renderOption={(props, option, { selected }) => (
            <Stack
              direction="row"
              component="li"
              {...props}
              sx={{
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Stack
                direction="row"
                sx={{ alignItems: 'center', minWidth: 0, flex: 1 }}
              >
                <Checkbox
                  disableRipple
                  checked={selected}
                  sx={{ mr: 2, p: 0, flexShrink: 0 }}
                />
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {option.label}
                </Box>
              </Stack>
              {option.status === 'INACTIVE' && <InactiveChip />}
            </Stack>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Teams"
              placeholder="Search for teams"
              fullWidth
            />
          )}
        />
      )}
    />
  );
};
